import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { getManualSoldOutImpacts, getManualSoldOutReport } from "services/scratcherService";
import type { ManualSoldOutImpact, ManualSoldOutReport } from "services/scratcherService";

const manualSoldOutReportQuery = (
  storeId: string | null | undefined,
  reportDate: string | null | undefined
) => ({
  queryKey: ["audit-dashboard-manual-sold-out-report", storeId, reportDate],
  queryFn: () => getManualSoldOutReport(storeId!, reportDate!),
  enabled: Boolean(storeId && reportDate),
  staleTime: 60_000,
  retry: false,
});

export function useAuditDashboardManualSoldOutReport(
  storeId: string | null | undefined,
  reportDate: string | null | undefined
) {
  return useQuery<ManualSoldOutReport, Error>(manualSoldOutReportQuery(storeId, reportDate));
}

export function useAuditDashboardManualSoldOutReports(
  storeId: string | null | undefined,
  reportDates: string[]
) {
  const results = useQueries({
    queries: reportDates.map((reportDate) => manualSoldOutReportQuery(storeId, reportDate)),
  });

  return useMemo(() => {
    const reportsByDate = new Map<string, ManualSoldOutReport>();
    results.forEach((result, index) => {
      if (result.data) {
        reportsByDate.set(reportDates[index], result.data);
      }
    });
    return reportsByDate;
  }, [reportDates, results]);
}

const manualSoldOutImpactQuery = (
  storeId: string | null | undefined,
  affectedDate: string | null | undefined
) => ({
  queryKey: ["audit-dashboard-manual-sold-out-impact", storeId, affectedDate],
  queryFn: () => getManualSoldOutImpacts(storeId!, affectedDate!),
  enabled: Boolean(storeId && affectedDate),
  staleTime: 60_000,
  retry: false,
});

export function useAuditDashboardManualSoldOutImpact(
  storeId: string | null | undefined,
  affectedDate: string | null | undefined
) {
  return useQuery<ManualSoldOutImpact, Error>(manualSoldOutImpactQuery(storeId, affectedDate));
}

export function useAuditDashboardManualSoldOutImpacts(
  storeId: string | null | undefined,
  affectedDates: string[]
) {
  const results = useQueries({
    queries: affectedDates.map((affectedDate) => manualSoldOutImpactQuery(storeId, affectedDate)),
  });

  return useMemo(() => {
    const impactsByDate = new Map<string, ManualSoldOutImpact>();
    results.forEach((result, index) => {
      if (result.data) {
        impactsByDate.set(affectedDates[index], result.data);
      }
    });
    return impactsByDate;
  }, [affectedDates, results]);
}
